import * as logClient from "@classdojo/log-client";
import { ref, StoreWithProps, useClassState, useSideEffectOnMount } from "@web-monorepo/hooks";
import { useEffect, useRef } from "react";

export type UseCountdownCardProps = {
  /** Initial countdown duration in seconds. */
  initialSeconds: number;
};

export type UseCountdownCardResult = {
  /** Remaining whole seconds, clamped to 0. */
  secondsRemaining: number;
  /** Icon to show on the central play/pause button. */
  icon: "play" | "pause";
  /** Stable click handler that toggles between pause/resume/reset based on state. */
  toggle: () => void;
  restart: () => void;
  incrementTimer: () => void;
  /** Ref the consuming component attaches to the alarm <audio> element. */
  audioRef: React.RefObject<HTMLAudioElement | null>;
};

const POLL_INTERVAL_MS = 100;
const INCREMENT_SECONDS = 10;

/**
 * Valtio-backed countdown state machine. Owns startTime / stoppedTime /
 * seconds + the polling interval that drives re-renders. On expiry, plays
 * the alarm <audio> (whose element is wired in by the consuming component
 * via the returned `audioRef`).
 */
class CountdownCardStore extends StoreWithProps<UseCountdownCardProps> {
  startTime: number;
  stoppedTime: number | null = null;
  seconds: number;
  /** Reactive bump on every poll tick — drives the secondsRemaining re-read. */
  tickCount = 0;

  /** Non-reactive container for the polling interval handle. */
  private poll = ref({ handle: null as ReturnType<typeof window.setInterval> | null });
  /** Audio element ref (assigned via React; not reactive). */
  audioRef = ref({ current: null as HTMLAudioElement | null });

  constructor(props: UseCountdownCardProps) {
    super(props);
    this.startTime = Date.now();
    this.seconds = props.initialSeconds;
  }

  get secondsRemaining(): number {
    return Math.max(Math.ceil(this.seconds - ((this.stoppedTime || Date.now()) - this.startTime) / 1000), 0);
  }

  get icon(): "play" | "pause" {
    if (this.secondsRemaining <= 0) return "play";
    return this.stoppedTime ? "play" : "pause";
  }

  start() {
    this.poll.handle = window.setInterval(() => this.tick(), POLL_INTERVAL_MS);
  }

  stop() {
    this.stopInterval();
  }

  toggle() {
    if (this.secondsRemaining <= 0) {
      this.resetTimer(this.seconds);
      return;
    }
    if (this.stoppedTime) {
      this.resumeTimer();
    } else {
      this.pauseTimer();
    }
  }

  restart() {
    this.resetTimer(this.seconds);
  }

  incrementTimer() {
    this.startTime = this.startTime + INCREMENT_SECONDS * 1000;
  }

  private tick() {
    // Bump a reactive counter so Valtio publishes a snapshot — the computed
    // getter `secondsRemaining` reads Date.now() which isn't reactive, so we
    // need an explicit reactive write to drive re-renders at the poll cadence.
    this.tickCount += 1;
    if (this.secondsRemaining <= 0) {
      // Stop the polling interval so the alarm doesn't loop every 100ms.
      // resetTimer re-arms the interval if the user restarts the timer.
      this.stopInterval();
      this.playAlarm();
      this.stoppedTime = null;
    }
  }

  private playAlarm() {
    this.audioRef.current?.play().catch(() => {
      logClient.sendMetrics([{ type: "increment", value: 1, metricName: "toolkit.media.audio.play.error" }]);
    });
  }

  private pauseTimer() {
    this.stoppedTime = Date.now();
  }

  private resumeTimer() {
    this.startTime = Date.now() - ((this.stoppedTime ?? 0) - this.startTime);
    this.stoppedTime = null;
  }

  private resetTimer(seconds: number) {
    this.stopInterval();
    this.seconds = seconds;
    this.startTime = Date.now();
    this.stoppedTime = null;
    this.poll.handle = window.setInterval(() => this.tick(), POLL_INTERVAL_MS);
  }

  private stopInterval() {
    if (this.poll.handle !== null) {
      window.clearInterval(this.poll.handle);
      this.poll.handle = null;
    }
  }
}

export function useCountdownCard(props: UseCountdownCardProps): UseCountdownCardResult {
  const store = useClassState(CountdownCardStore, props);
  const { start, stop } = store;

  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  // Mirror the store's internal audio ref onto a regular React ref the consumer
  // can bind to <audio ref={audioRef}>. We can't return store.audioRef directly
  // because the snapshot wrapper would freeze it. CountdownCard renders <audio>
  // unconditionally, so a single mount-time sync is sufficient.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  useSideEffectOnMount(() => {
    store.audioRef.current = audioRef.current;
  });

  return {
    secondsRemaining: store.secondsRemaining,
    icon: store.icon,
    toggle: store.toggle,
    restart: store.restart,
    incrementTimer: store.incrementTimer,
    audioRef,
  };
}
